import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import {
  Plus,
  Receipt,
  MoreHorizontal,
  Pencil,
  Bell,
  XCircle,
  AlertTriangle,
  Clock,
  Download,
  Timer,
  FileText,
  PhoneCall,
  CalendarPlus,
  CalendarDays,
} from "lucide-react";
import {
  getAppointments,
  getDoctors,
  getDepartments,
  getAppointmentAgents,
  getDoctorAttendanceToday,
  checkInAppointment,
  cancelAppointment,
  remindAppointment,
  markAppointmentContacted,
  clickToCall,
} from "../../api/api";
import { getSocket } from "../../lib/socket";
import DataTable from "../../components/DataTable";
import EditBookingModal from "../../components/EditBookingModal";
import GenerateInvoiceModal from "../../components/GenerateInvoiceModal";
import DispositionModal from "../../components/DispositionModal";
import ConfirmModal from "../../components/ConfirmModal";
import AppointmentFilters from "../../components/AppointmentFilters";
import MarkDelayModal from "../../components/MarkDelayModal";
import MarkContactedModal from "../../components/MarkContactedModal";
import SegmentedControl from "../../components/SegmentedControl";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  APPOINTMENT_STATUS,
  ACTIVE_STATUSES,
  STATUS_LABEL,
  VISIT_TYPE_LABEL,
  DELAY_STATUS,
  DELAY_STATUS_LABEL,
  MISSED_STATUS,
  DELAYED_STATUS,
} from "../../lib/appointment-filters";
import { exportAppointments } from "../../lib/appointment-export";
import { cn } from "../../lib/utils";

const TABS = [
  { value: "upcoming", label: "Upcoming", statuses: ACTIVE_STATUSES, countKey: "upcoming" },
  { value: "delay", label: "Delay", statuses: [DELAYED_STATUS], countKey: "delay" },
  { value: "missed", label: "Missed", statuses: [MISSED_STATUS], countKey: "missed" },
  { value: "done", label: "Done", statuses: [APPOINTMENT_STATUS.COMPLETED], countKey: "done" },
  { value: "cancelled", label: "Cancelled", statuses: [APPOINTMENT_STATUS.CANCELLED], countKey: "cancelled" },
  { value: "all", label: "All", statuses: null, countKey: "all" },
];

// Sent when the chosen statuses can't occur inside the active tab (e.g.
// "Checked In" while viewing Cancelled). It matches no row, which is the
// truthful answer, rather than silently dropping the filter.
const MATCH_NOTHING = "__none__";

const STATUS_VARIANT = {
  [APPOINTMENT_STATUS.PENDING]: "outline",
  [APPOINTMENT_STATUS.CREATED]: "outline",
  [APPOINTMENT_STATUS.CONFIRMED]: "outline",
  [APPOINTMENT_STATUS.RESCHEDULED]: "warning",
  [APPOINTMENT_STATUS.DELAYED]: "warning",
  [APPOINTMENT_STATUS.CHECKED_IN]: "secondary",
  [APPOINTMENT_STATUS.COMPLETED]: "secondary",
  [APPOINTMENT_STATUS.CANCELLED]: "destructive",
};

// Only outcomes that need attention get colour; the rest stay quiet so the
// row's own status badge keeps the emphasis.
const DELAY_TONE = {
  [DELAY_STATUS.PROVIDER_DELAYED]: "text-warning",
  [DELAY_STATUS.PATIENT_DELAYED]: "text-warning",
  [DELAY_STATUS.PROVIDER_NO_SHOW]: "text-destructive",
  [DELAY_STATUS.PATIENT_NO_SHOW]: "text-destructive",
  [DELAY_STATUS.MEETING_COMPLETED]: "text-muted-foreground",
};

const todayISO = () => moment().format("YYYY-MM-DD");

// How far past its slot a running-late (`running_late`) appointment is —
// "25 min", or "1 hr 5 min" / "2 hr" once it's an hour or more.
const formatLate = (a) => {
  const minutes = Math.max(1, moment().diff(moment().startOf("day").add(a.slot_minutes, "minutes"), "minutes"));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${minutes} min`;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};

const EMPTY_FILTERS = {
  search: "",
  from: todayISO(),
  to: todayISO(),
  doctorId: "",
  departmentIds: [],
  statuses: [],
  visitTypes: [],
  delayStatuses: [],
  agentIds: [],
};

// ---- URL <-> state -------------------------------------------------------
// Filters live in the query string so a filtered view can be bookmarked,
// shared with a colleague, and survives a refresh or a trip into a detail page.
// With no date in the URL, default to today rather than "Any" so a fresh
// visit to the page isn't showing every appointment ever booked.
const readFilters = (params) => ({
  search: params.get("q") || "",
  from: params.get("from") || (params.has("to") ? "" : todayISO()),
  to: params.get("to") || (params.has("from") ? "" : todayISO()),
  doctorId: params.get("doctor") || "",
  departmentIds: params.get("department")?.split(",").filter(Boolean) || [],
  statuses: params.get("status")?.split(",").filter(Boolean) || [],
  visitTypes: params.get("type")?.split(",").filter(Boolean) || [],
  delayStatuses: params.get("delay")?.split(",").filter(Boolean) || [],
  agentIds: params.get("agent")?.split(",").filter(Boolean) || [],
});

const writeParams = (filters, tab, page) => {
  const next = new URLSearchParams();
  if (filters.search) next.set("q", filters.search);
  if (filters.from) next.set("from", filters.from);
  if (filters.to) next.set("to", filters.to);
  if (filters.doctorId) next.set("doctor", filters.doctorId);
  if (filters.departmentIds.length) next.set("department", filters.departmentIds.join(","));
  if (filters.statuses.length) next.set("status", filters.statuses.join(","));
  if (filters.visitTypes.length) next.set("type", filters.visitTypes.join(","));
  if (filters.delayStatuses.length) next.set("delay", filters.delayStatuses.join(","));
  if (filters.agentIds.length) next.set("agent", filters.agentIds.join(","));
  if (tab !== "upcoming") next.set("tab", tab);
  if (page > 1) next.set("page", String(page));
  return next;
};

function initialsOf(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function Appointments() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readFilters(searchParams));
  const [tab, setTab] = useState(() => searchParams.get("tab") || "upcoming");
  const [page, setPage] = useState(() => Number(searchParams.get("page")) || 1);

  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [checkedInDoctorIds, setCheckedInDoctorIds] = useState(() => new Set());
  const [departments, setDepartments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [counts, setCounts] = useState({ upcoming: 0, done: 0, cancelled: 0, all: 0 });

  const [editingAppointment, setEditingAppointment] = useState(null);
  const [invoicingAppointment, setInvoicingAppointment] = useState(null);
  const [dispositionTarget, setDispositionTarget] = useState(null); // { appointment, action }
  const [checkInTarget, setCheckInTarget] = useState(null);
  const [contactTarget, setContactTarget] = useState(null);
  const [delayOpen, setDelayOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [callingId, setCallingId] = useState(null);
  const clickToCallEnabled = useSelector((s) => s.settings.data?.features?.click_to_call_enabled !== false);

  // Only starts the call (MCube rings the agent first, then bridges in the
  // patient) — the answered/missed toast comes later from the app-wide
  // "call_status_update" socket listener in App.js once MCube's webhook
  // reports the real outcome, same as the Patients page's call button.
  const handleQuickCall = async (patientId, e) => {
    e.stopPropagation();
    setCallingId(patientId);
    try {
      await clickToCall(patientId);
      toast("Calling — you'll be rung first, then connected to the patient.");
    } catch (err) {
      toast.error(err?.message || "Could not place call.");
    } finally {
      setCallingId(null);
    }
  };

  // Any filter change invalidates the current page.
  const patch = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);

  // Every appointment this doctor has today, whatever its status — so the
  // other filters are cleared and the tab moves to "All".
  const showDoctorToday = (doctorId) => {
    const today = moment().format("YYYY-MM-DD");
    setFilters({ ...EMPTY_FILTERS, doctorId, from: today, to: today });
    setTab("all");
    setPage(1);
  };

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const hasFilters = useMemo(
    () =>
      Boolean(filters.search || filters.from || filters.to || filters.doctorId) ||
      filters.departmentIds.length > 0 ||
      filters.statuses.length > 0 ||
      filters.visitTypes.length > 0 ||
      filters.delayStatuses.length > 0 ||
      filters.agentIds.length > 0,
    [filters]
  );

  // The status filter narrows *within* the active tab rather than replacing
  // it, so "Upcoming + Checked In" means what it looks like it means.
  const effectiveStatuses = useMemo(() => {
    const tabStatuses = TABS.find((t) => t.value === tab)?.statuses;
    if (!filters.statuses.length) return tabStatuses;
    if (!tabStatuses) return filters.statuses;
    const intersection = filters.statuses.filter((s) => tabStatuses.includes(s));
    return intersection.length ? intersection : [MATCH_NOTHING];
  }, [tab, filters.statuses]);

  useEffect(() => {
    // Guarded: react-router recreates setSearchParams whenever searchParams
    // changes, so writing unconditionally would retrigger this effect forever.
    // Comparing the serialised form first makes the write a no-op once settled.
    const next = writeParams(filters, tab, page).toString();
    if (next !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, tab, page, searchParams, setSearchParams]);

  useEffect(() => {
    getDoctors({ limit: 200, include_inactive: true })
      .then((res) => setDoctors(res.data || []))
      .catch(() => setDoctors([]));
    getDepartments({ active: true, limit: 100 })
      .then((res) => setDepartments(res.data || []))
      .catch(() => setDepartments([]));
    getAppointmentAgents()
      .then((res) => setAgents(res.data || []))
      .catch(() => setAgents([]));
  }, []);

  // Which doctors are physically in the building right now, for the green
  // "checked in" dot next to their name in the Doctor column — "checked in"
  // means present today and not yet checked out (see doctor-attendance.js).
  const loadDoctorAttendance = useCallback(() => {
    getDoctorAttendanceToday()
      .then((res) => {
        const inBuilding = (res.data || [])
          .filter((d) => d.checked_in_at && !d.checked_out_at)
          .map((d) => String(d._id));
        setCheckedInDoctorIds(new Set(inBuilding));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDoctorAttendance();
  }, [loadDoctorAttendance]);

  // Live updates when the front desk checks a doctor in/out from the
  // Doctor Attendance page, plus a safety poll in case a socket event is
  // ever dropped — this page tends to stay open all day.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    socket.on("doctor_attendance:updated", loadDoctorAttendance);
    return () => socket.off("doctor_attendance:updated", loadDoctorAttendance);
  }, [loadDoctorAttendance]);

  useEffect(() => {
    const interval = setInterval(loadDoctorAttendance, 120000);
    return () => clearInterval(interval);
  }, [loadDoctorAttendance]);

  // Shared so Export sends exactly the same filters the table is showing.
  const queryParams = useMemo(
    () => ({
      status: effectiveStatuses?.length ? effectiveStatuses.join(",") : undefined,
      visit_type: filters.visitTypes.length ? filters.visitTypes.join(",") : undefined,
      delay_status: filters.delayStatuses.length ? filters.delayStatuses.join(",") : undefined,
      doctor_id: filters.doctorId || undefined,
      department_id: filters.departmentIds.length ? filters.departmentIds.join(",") : undefined,
      agent_id: filters.agentIds.length ? filters.agentIds.join(",") : undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      search: filters.search || undefined,
    }),
    [effectiveStatuses, filters]
  );

  const load = useCallback(() => {
    setLoading(true);
    return getAppointments({ ...queryParams, page, limit: 15 })
      .then((res) => {
        setAppointments(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
        if (res.counts) setCounts(res.counts);
      })
      .catch((err) => toast.error(err?.message || "Could not load appointments."))
      .finally(() => setLoading(false));
  }, [queryParams, page]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Exports every row matching the current filters, not just the visible page
  // — a 15-row export of a 200-row result would be quietly wrong.
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getAppointments({ ...queryParams, page: 1, limit: 5000 });
      const rows = res.data || [];
      if (rows.length === 0) {
        toast("No appointments to export for these filters.");
        return;
      }
      const written = exportAppointments(rows);
      toast.success(`Exported ${written} appointment${written === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err?.message || "Could not export appointments.");
    } finally {
      setExporting(false);
    }
  };

  const runAction = (fn, successMsg) => async (appointment) => {
    try {
      await fn(appointment._id);
      toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(err?.message || "Action failed.");
    }
  };

  const handleCheckIn = runAction(checkInAppointment, "Patient checked in.");
  const handleRemind = runAction(remindAppointment, "Reminder sent via WhatsApp.");

  // Only ever "cancel" now — completion isn't a manual disposition anymore,
  // it happens automatically (checking in the next patient closes out the
  // previous one; the last patient of the day times out after 30 min idle).
  const handleDispositionConfirm = async (appointment, { disposition, note }) => {
    try {
      await cancelAppointment(appointment._id, { disposition, note });
      toast.success("Appointment cancelled.");
      load();
    } catch (err) {
      toast.error(err?.message || "Action failed.");
    }
  };

  const handleMarkContacted = async (appointment, disposition) => {
    try {
      await markAppointmentContacted(appointment._id, { disposition });
      toast.success("Marked as contacted.");
      load();
    } catch (err) {
      toast.error(err?.message || "Could not save.");
    }
  };

  const isActive = (a) => ACTIVE_STATUSES.includes(a.status);

  // Check In (or Mark Contacted, once missed) — the row's one-tap action.
  // A running-late patient gets both: the desk usually phones them to see
  // whether they're still coming, but they can still walk in and check in.
  const renderPrimaryAction = (a, className) => {
    if (!isActive(a) || a.on_leave || a.status === APPOINTMENT_STATUS.CHECKED_IN) return null;
    const contactButton = (
      <Button size="sm" variant="outline" className={cn("bg-card", className)} onClick={() => setContactTarget(a)}>
        <PhoneCall className="h-3.5 w-3.5" /> Mark Contacted
      </Button>
    );
    if (a.missed) return contactButton;
    const checkInButton = (
      <Button size="sm" variant="outline" className={cn("bg-card", className)} onClick={() => setCheckInTarget(a)}>
        Check In
      </Button>
    );
    if (!a.running_late) return checkInButton;
    // Stacked, one above the other, at equal width — side by side they
    // squeezed the Status column and wrapped its delay badge.
    return (
      <div className="flex flex-col items-stretch gap-1.5">
        {contactButton}
        {checkInButton}
      </div>
    );
  };

  const columns = [
    {
      key: "id",
      header: "ID",
      render: (a) => (
        <span className="whitespace-nowrap rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
          #{a._id.slice(-6)}
        </span>
      ),
    },
    {
      key: "patient",
      header: "Patient",
      render: (a) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-xs font-semibold text-secondary">
            {initialsOf(a.patient_id?.name)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium leading-tight">{a.patient_id?.name}</p>
            <div className="flex items-center gap-1">
              <p className="text-xs leading-tight text-muted-foreground">{a.patient_id?.phone}</p>
              {clickToCallEnabled && a.patient_id?._id && (
                <button
                  type="button"
                  title="Call patient"
                  disabled={callingId === a.patient_id._id}
                  onClick={(e) => handleQuickCall(a.patient_id._id, e)}
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-secondary/10 hover:text-secondary disabled:opacity-50"
                >
                  <PhoneCall className="h-3 w-3" />
                </button>
              )}
            </div>
            {renderPrimaryAction(a, "mt-1.5 h-8 px-2.5 text-xs sm:hidden")}
          </div>
        </div>
      ),
    },
    {
      key: "datetime",
      header: "Date & Time",
      render: (a) => (
        <div>
          <p className="flex items-center gap-1.5 font-medium leading-tight">
            <Clock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            {
              // A walk-in's slot_time is just a booking-time placeholder
              // (see create() on the backend) — once they're actually
              // checked in, the real, meaningful time is when that
              // happened, so show that instead.
              a.visit_type === "walkin" && a.checked_in_at
                ? moment(a.checked_in_at).format("hh:mm A")
                : a.slot_time
            }
            {a.is_vip && (
              <Badge variant="warning" className="px-1.5 py-0 text-[10px]">
                VIP
              </Badge>
            )}
          </p>
          <p className="text-xs leading-tight text-muted-foreground">{a.iso_date}</p>
          {a.created_by?.name && (
            <p className="mt-0.5 text-xs text-muted-foreground">Booked by {a.created_by.name}</p>
          )}
          {a.last_edited_by_name && (
            <p className="mt-0.5 text-xs text-muted-foreground" title={a.last_disposition}>
              Edited by {a.last_edited_by_name}
            </p>
          )}
          {a.status === APPOINTMENT_STATUS.CANCELLED && a.cancelled_by_name && (
            <p className="mt-0.5 text-xs text-muted-foreground">Cancelled by {a.cancelled_by_name}</p>
          )}
          {a.disposition && !isActive(a) && a.status !== APPOINTMENT_STATUS.CANCELLED && (
            <p className="text-xs text-muted-foreground">{a.disposition}</p>
          )}
        </div>
      ),
    },
    {
      key: "doctor",
      header: "Doctor",
      render: (a) => (
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate text-sm">
            {checkedInDoctorIds.has(String(a.doctor_id?._id)) && (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-secondary"
                title="Checked in today"
                aria-label="Checked in today"
              />
            )}
            {a.doctor_id?._id ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="truncate text-left hover:text-primary hover:underline">
                    {a.doctor_id.name}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => navigate(`/opd?doctor=${a.doctor_id._id}`)}>
                    <CalendarPlus className="mr-2 h-4 w-4" /> Book a slot
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => showDoctorToday(a.doctor_id._id)}>
                    <CalendarDays className="mr-2 h-4 w-4" /> Today's appointments
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="truncate">{a.doctor_id?.name}</span>
            )}
          </p>
          {(a.doctor_id?.department_id?.name || a.doctor_id?.specialty) && (
            <p className="truncate text-xs text-muted-foreground">
              {[a.doctor_id?.department_id?.name, a.doctor_id?.specialty].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "procedure",
      header: "Procedure",
      render: (a) => <span className="text-sm text-muted-foreground">{a.procedure}</span>,
    },
    // Only meaningful on tabs that can actually show a cancelled row — every
    // other tab (Upcoming/Missed/Done) excludes cancelled appointments by
    // definition, so this would just be a column of dashes there.
    // "Cancelled by" itself lives inline in the Date & Time column above,
    // next to "Booked by" and "Edited by", rather than as its own column.
    ...(tab === "cancelled" || tab === "all"
      ? [
          {
            key: "cancelled_reason",
            header: "Cancelled Reason",
            render: (a) =>
              a.status === APPOINTMENT_STATUS.CANCELLED ? (
                <span className="text-sm text-muted-foreground">
                  {[a.disposition, a.cancellation_note].filter(Boolean).join(" — ") || "—"}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              ),
          },
        ]
      : []),
    {
      key: "status",
      header: "Status",
      render: (a) => {
        // The last edit_history entry marked `contacted` (see markContacted
        // on the backend) is the desk's note from following up on a missed
        // or running-late appointment — distinct from a real reschedule/edit, so it gets its
        // own icon+note here rather than folding into "Edited by X".
        const contactEntry = [...(a.edit_history || [])].reverse().find((e) => e.changes?.contacted);

        if (a.on_leave && isActive(a)) {
          return (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" /> On leave — reschedule
            </Badge>
          );
        }

        if (a.missed && isActive(a)) {
          return (
            <div className="space-y-1">
              <Badge variant="destructive" className="gap-1">
                <Clock className="h-3 w-3" /> Missed
              </Badge>
              {contactEntry && (
                <div className="flex items-start gap-1 text-xs text-muted-foreground">
                  <PhoneCall className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" aria-label="Contacted" />
                  <span>{contactEntry.disposition}</span>
                </div>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={STATUS_VARIANT[a.status] || "outline"}>
                {STATUS_LABEL[a.status] || a.status}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {VISIT_TYPE_LABEL[a.visit_type] || a.visit_type}
              </span>
            </div>
            {a.running_late && (
              <Badge variant="warning" className="gap-1">
                <Clock className="h-3 w-3" /> Delay · {formatLate(a)}
              </Badge>
            )}
            {a.running_late && contactEntry && (
              <div className="flex items-start gap-1 text-xs text-muted-foreground">
                <PhoneCall className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" aria-label="Contacted" />
                <span>{contactEntry.disposition}</span>
              </div>
            )}
            {a.delay_status && (
              <p className={cn("text-xs font-medium", DELAY_TONE[a.delay_status])}>
                {DELAY_STATUS_LABEL[a.delay_status]}
              </p>
            )}
            {/* A cancelled appointment may have been marked contacted while it
                was still sitting in Missed — that note stays relevant context
                for why/how it ended up cancelled, so it carries over here too. */}
            {a.status === APPOINTMENT_STATUS.CANCELLED && contactEntry && (
              <div className="flex items-start gap-1 text-xs text-muted-foreground">
                <PhoneCall className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" aria-label="Contacted" />
                <span>{contactEntry.disposition}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (a) => {
        const canRxInvoice = a.status !== APPOINTMENT_STATUS.CANCELLED;
        const canActiveActions = isActive(a) && !a.on_leave;
        return (
          <div className="flex items-center justify-end gap-2">
            {/* below `sm` this sits under the patient's name instead */}
            {renderPrimaryAction(a, "hidden sm:inline-flex")}
            {(canRxInvoice || isActive(a)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" aria-label="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canRxInvoice && (
                    <DropdownMenuItem onClick={() => navigate(`/prescriptions/${a._id}`)}>
                      <FileText className="mr-2 h-4 w-4" /> Rx
                    </DropdownMenuItem>
                  )}
                  {canRxInvoice && (
                    <DropdownMenuItem onClick={() => setInvoicingAppointment(a)}>
                      <Receipt className="mr-2 h-4 w-4" /> Invoice
                    </DropdownMenuItem>
                  )}
                  {canRxInvoice && isActive(a) && <DropdownMenuSeparator />}
                  {canActiveActions && a.missed && a.status !== APPOINTMENT_STATUS.CHECKED_IN && (
                    <DropdownMenuItem onClick={() => setCheckInTarget(a)}>
                      <Clock className="mr-2 h-4 w-4" /> Check In
                    </DropdownMenuItem>
                  )}
                  {canActiveActions && a.missed && <DropdownMenuSeparator />}
                  {isActive(a) && (
                    <DropdownMenuItem onClick={() => setEditingAppointment(a)}>
                      <Pencil className="mr-2 h-4 w-4" /> {a.on_leave ? "Reschedule" : "Edit"}
                    </DropdownMenuItem>
                  )}
                  {canActiveActions && (
                    <DropdownMenuItem onClick={() => handleRemind(a)}>
                      <Bell className="mr-2 h-4 w-4" /> Send reminder
                    </DropdownMenuItem>
                  )}
                  {isActive(a) && <DropdownMenuSeparator />}
                  {isActive(a) && (
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDispositionTarget({ appointment: a, action: "cancel" })}
                    >
                      <XCircle className="mr-2 h-4 w-4" /> Cancel appointment
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      },
    },
  ];

  const tabItems = TABS.map((t) => ({ ...t, count: counts[t.countKey] ?? 0 }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Appointments</h2>
          <p className="text-sm text-muted-foreground">
            Track, filter and manage every appointment across your clinic
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="bg-card" onClick={handleExport} disabled={exporting}>
            <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export"}
          </Button>
          <Button variant="outline" className="bg-card" onClick={() => setDelayOpen(true)}>
            <Timer className="h-4 w-4" /> Mark Delay
          </Button>
          <Button onClick={() => navigate("/opd")}>
            <Plus className="h-4 w-4" /> Book
          </Button>
        </div>
      </div>

      <AppointmentFilters
        value={filters}
        patch={patch}
        onReset={resetFilters}
        doctors={doctors}
        departments={departments}
        agents={agents}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          items={tabItems}
          value={tab}
          onChange={(v) => {
            setTab(v);
            setPage(1);
          }}
        />
        {!loading && (
          <p className="text-sm text-muted-foreground">
            {meta.total} {meta.total === 1 ? "result" : "results"}
          </p>
        )}
      </div>

      <DataTable
        columns={columns}
        data={appointments}
        loading={loading}
        emptyMessage={
          hasFilters
            ? "No appointments match these filters."
            : "No appointments here yet."
        }
        emptyAction={
          hasFilters ? (
            <Button variant="outline" size="sm" onClick={resetFilters}>
              Clear all filters
            </Button>
          ) : (
            <Button size="sm" onClick={() => navigate("/opd")}>
              <Plus className="h-4 w-4" /> Book an appointment
            </Button>
          )
        }
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(a) => a._id}
      />

      <MarkDelayModal open={delayOpen} onOpenChange={setDelayOpen} onSaved={load} />

      <EditBookingModal
        open={!!editingAppointment}
        onOpenChange={(v) => !v && setEditingAppointment(null)}
        appointment={editingAppointment}
        onSaved={load}
      />

      <GenerateInvoiceModal
        open={!!invoicingAppointment}
        onOpenChange={(v) => !v && setInvoicingAppointment(null)}
        appointment={invoicingAppointment}
      />

      <DispositionModal
        open={!!dispositionTarget}
        onOpenChange={(v) => !v && setDispositionTarget(null)}
        appointment={dispositionTarget?.appointment}
        action={dispositionTarget?.action}
        onConfirm={handleDispositionConfirm}
      />

      <ConfirmModal
        open={!!checkInTarget}
        onOpenChange={(v) => !v && setCheckInTarget(null)}
        title="Check in patient?"
        description={
          checkInTarget &&
          `Confirm ${checkInTarget.patient_id?.name} has arrived for their ${checkInTarget.slot_time} appointment with ${checkInTarget.doctor_id?.name}.`
        }
        confirmLabel="Check In"
        onConfirm={() => handleCheckIn(checkInTarget)}
      />

      <MarkContactedModal
        open={!!contactTarget}
        onOpenChange={(v) => !v && setContactTarget(null)}
        appointment={contactTarget}
        onMarkContacted={handleMarkContacted}
      />
    </div>
  );
}
